import { SegmentEvent } from '@segment/analytics-next'

// Type-only import: erased at compile time, so no react-native runtime code ends up in the web bundle.
import type { SegmentClient } from '@segment/analytics-react-native'

import { makeLogger } from '~/utils'

const logger = makeLogger('AnalyticsService', '#f44c7f')
export const analyticsLogger = logger

const info = (event: SegmentEvent) => {
  logger.groupCollapsed(
    `(${event.type.toUpperCase()})`,
    event.event || event.context.page.title,
  )
  logger.info('data:', event.traits || event.properties)
  logger.info('event context:', event.context)
  logger.info('full event:', event)
  logger.groupEnd()
  return event
}

const identify = (event: SegmentEvent) => {
  logger.groupCollapsed(
    `(${event.type.toUpperCase()})`,
    event.traits?.name || event.properties?.name,
  )
  logger.info('data:', event.traits || event.properties)
  logger.info('event context:', event.context)
  logger.info('full event:', event)
  logger.groupEnd()
  return event
}

export const loggerMiddleware = ({ payload, next }) => {
  const data = payload.obj
  switch (data.type) {
    case 'identify':
    case 'group':
    case 'alias':
      identify(data)
      break
    default:
      info(data)
      break
  }
  next(payload)
}

interface Config {
  writeKey: string
  debug?: boolean
  logger?: any
}

type WebSegmentClient = Omit<SegmentClient, 'screen'> & {
  page: SegmentClient['screen']
  addSourceMiddleware: (...args: any[]) => void
}

type Win = Window &
  typeof globalThis & {
    analytics: WebSegmentClient
  }

/**
 * Create an anlytics client to be used throughout the application.
 * This reproduces the same API as the native client.
 */
let analytics: SegmentClient

const win = window as Win

// To provide analytics, we expect the `analytics` object to be present
// globally. This is acheived by placing the Segment script in the HTML page
// (see app/web/index.html).
// TODO Rather tha using the segment script, we can initialize the client
// directly ourselves using the NPM library. This would avoid referencing the
// global variable and make this code less brittle.
// See https://github.com/segmentio/analytics-next/tree/master/packages/browser#-using-as-an-npm-package
export function createClient(options: Config) {
  if (analytics) {
    return analytics
  }

  if (options.debug) {
    win.analytics.addSourceMiddleware(loggerMiddleware)
  }

  // If you need really verbose logging, turn this on:
  // win.analytics.debug(true)

  return {
    identify: (...args: Parameters<SegmentClient['identify']>) =>
      win.analytics.identify(...args),
    group: (...args: Parameters<SegmentClient['group']>): any =>
      win.analytics.group(...args),
    alias: (...args: Parameters<SegmentClient['alias']>) =>
      win.analytics.alias(...args),
    track: (...args: Parameters<SegmentClient['track']>) =>
      win.analytics.track(...args),
    // Forward `screen` to `page` for compatibility with native.
    screen: (...args: Parameters<SegmentClient['screen']>) =>
      win.analytics.page(...args),
    reset: (...args: Parameters<SegmentClient['reset']>) =>
      win.analytics.reset(...args),
  } as SegmentClient
}
